import React, { useEffect, useState, useRef } from "react";
import "./EditarPerfilPopup.css";
import PopupMensagem from "./PopupMensagem";
import { auth, db, storage } from "../firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

function EditarPerfilPopup({ onClose }) {
  const [dadosUsuario, setDadosUsuario] = useState({});
  const [primeiroNome, setPrimeiroNome] = useState("");
  const [ultimoNome, setUltimoNome] = useState("");
  const [estado, setEstado] = useState("");
  const [fotoPerfilFile, setFotoPerfilFile] = useState(null);
  const [fotoCapaFile, setFotoCapaFile] = useState(null);
  const [fotoPerfilURL, setFotoPerfilURL] = useState("");
  const [fotoCapaURL, setFotoCapaURL] = useState("");
  const [popupMsg, setPopupMsg] = useState(null);
  const [especialista, setEspecialista] = useState(false);
  const [sobreMim, setSobreMim] = useState("");
  const [linkParaSeguir, setLinkParaSeguir] = useState("");
  const [denominacao, setDenominacao] = useState("");

  // novo: estado de erros por campo
  const [errors, setErrors] = useState({});

  // refs para rolar até o primeiro erro
  const refs = {
    primeiroNome: useRef(null),
    ultimoNome: useRef(null),
    estado: useRef(null),
    fotoPerfil: useRef(null),
    fotoCapa: useRef(null),
    sobreMim: useRef(null),
    linkParaSeguir: useRef(null),
    denominacao: useRef(null),
  };

  useEffect(() => {
    const carregarDados = async () => {
      const user = auth.currentUser;
      if (!user) return;

      const refUser = doc(db, "usuarios", user.uid);
      const snap = await getDoc(refUser);

      if (snap.exists()) {
        const dados = snap.data();
        setDadosUsuario(dados);
        setPrimeiroNome(dados.nome || "");
        setUltimoNome(dados.sobrenome || "");
        setEstado(dados.estado || "");
        setFotoPerfilURL(dados.fotoPerfil || "");
        setFotoCapaURL(dados.fotoCabecalho || "");
        if (dados.especialista) {
          setEspecialista(true);
          setSobreMim(dados.sobreMim || "");
          setLinkParaSeguir(dados.linkParaSeguir || "");
          setDenominacao(dados.denominacao || "");
        }
      }
    };
    carregarDados();
  }, []);

  // validação detalhada
  const validarCamposDetalhada = () => {
    const newErrors = {};
    const missing = [];

    const precisaFotoPerfil = !fotoPerfilURL && !fotoPerfilFile;
    const precisaFotoCapa = !fotoCapaURL && !fotoCapaFile;

    if (!primeiroNome?.trim()) {
      newErrors.primeiroNome = "Primeiro nome é obrigatório.";
      missing.push("Primeiro Nome");
    }
    if (!ultimoNome?.trim()) {
      newErrors.ultimoNome = "Último nome é obrigatório.";
      missing.push("Último Nome");
    }
    if (!estado?.trim()) {
      newErrors.estado = "Estado é obrigatório.";
      missing.push("Estado");
    }
    if (precisaFotoPerfil) {
      newErrors.fotoPerfil = "Foto do usuário é obrigatória.";
      missing.push("Foto do Usuário");
    }
    if (precisaFotoCapa) {
      newErrors.fotoCapa = "Foto de capa é obrigatória.";
      missing.push("Foto de Capa");
    }

    if (especialista) {
      if (!sobreMim?.trim()) {
        newErrors.sobreMim = "Sobre mim é obrigatório para especialista.";
        missing.push("Sobre Mim");
      } else if ((sobreMim || "").length > 600) {
        newErrors.sobreMim = "Texto excede 600 caracteres.";
      }
      if (!linkParaSeguir?.trim()) {
        newErrors.linkParaSeguir = "Link para seguir é obrigatório.";
        missing.push("Link para Seguir");
      }
      if (!denominacao?.trim()) {
        newErrors.denominacao = "Denominação é obrigatória.";
        missing.push("Denominação");
      }
    }

    return {
      ok: Object.keys(newErrors).length === 0,
      errors: newErrors,
      missing,
    };
  };

  // rola até o primeiro erro visível
  const scrollToFirstError = (errs) => {
    const ordem = [
      "primeiroNome",
      "ultimoNome",
      "estado",
      "fotoPerfil",
      "fotoCapa",
      "sobreMim",
      "linkParaSeguir",
      "denominacao",
    ];
    for (const key of ordem) {
      if (errs[key] && refs[key]?.current) {
        refs[key].current.scrollIntoView({ behavior: "smooth", block: "center" });
        break;
      }
    }
  };

  const limparErroCampo = (campo) => {
    if (errors[campo]) {
      setErrors((prev) => {
        const n = { ...prev };
        delete n[campo];
        return n;
      });
    }
  };

  const salvar = async () => {
    const { ok, errors: foundErrors, missing } = validarCamposDetalhada();

    if (!ok) {
      setErrors(foundErrors);

      // mensagem clara com lista dos campos que faltam
      if (missing.length) {
        setPopupMsg(
          `Faltam estes campos obrigatórios:\n• ${missing.join("\n• ")}`
        );
      } else {
        setPopupMsg("Verifica os campos destacados.");
      }

      scrollToFirstError(foundErrors);
      return;
    }

    const user = auth.currentUser;
    if (!user) return;

    const uploads = {};

    if (fotoPerfilFile) {
      const refPerfil = ref(storage, `fotosUsuarios/${user.uid}/perfil.jpg`);
      await uploadBytes(refPerfil, fotoPerfilFile);
      uploads.fotoPerfil = await getDownloadURL(refPerfil);
    }

    if (fotoCapaFile) {
      const refCapa = ref(storage, `fotosUsuarios/${user.uid}/capa.jpg`);
      await uploadBytes(refCapa, fotoCapaFile);
      uploads.fotoCabecalho = await getDownloadURL(refCapa);
    }

    const dadosParaSalvar = {
      nome: primeiroNome.trim(),
      sobrenome: ultimoNome.trim(),
      estado: estado.trim(),
      ...uploads,
    };

    if (especialista) {
      dadosParaSalvar.sobreMim = sobreMim.trim();
      dadosParaSalvar.linkParaSeguir = linkParaSeguir.trim();
      dadosParaSalvar.denominacao = denominacao.trim();
    }

    await setDoc(doc(db, "usuarios", user.uid), dadosParaSalvar, { merge: true });
    setPopupMsg("Dados atualizados com sucesso!\nAtualizações no perfil podem levar até algumas horas.");
    setTimeout(() => {
      onClose();
    }, 2000);
  };

  return (
    <div className="popup2-editar-perfil">
      <button onClick={onClose} className="btn-fechar2">X</button>
      <div className="popup2-box">
        <div className="popup2-header">
          <h2>EDITAR PERFIL</h2>
        </div>

        <div className="linha-fotos">
          <div className="foto-box" ref={refs.fotoPerfil}>
            <label>
              FOTO DO USUÁRIO
              <div className="textoeditar2">Max - 2mb</div>
            </label>
            {fotoPerfilURL && (
              <img src={fotoPerfilURL} alt="Perfil" className="foto-arredondada" />
            )}
            <input
              type="file"
              accept="image/*"
              id="input-foto-perfil"
              style={{ display: "none" }}
              onChange={(e) => {
                const file = e.target.files[0];
                if (file && file.size > 2 * 1024 * 1024) {
                  setPopupMsg("A imagem de perfil ultrapassa 2MB.");
                  return;
                }
                setFotoPerfilFile(file || null);
                if (file) {
                  setFotoPerfilURL(URL.createObjectURL(file));
                } else {
                  setFotoPerfilURL("");
                }
                limparErroCampo("fotoPerfil");
              }}
            />
            <button
              className={`upload-btn ${errors.fotoPerfil ? "input-erro" : ""}`}
              onClick={() => document.getElementById("input-foto-perfil").click()}
              aria-invalid={!!errors.fotoPerfil}
            >
              UPLOAD
            </button>
            <button
              className="delete-btn"
              onClick={() => {
                setFotoPerfilFile(null);
                setFotoPerfilURL("");
                // marcar erro se remover e não houver outra imagem
                setErrors((prev) => ({ ...prev, fotoPerfil: "Foto do usuário é obrigatória." }));
              }}
            >
              DELETE
            </button>
            {errors.fotoPerfil && <div className="hint-erro">{errors.fotoPerfil}</div>}
          </div>

          <div className="foto-box" ref={refs.fotoCapa}>
            <label>
              FOTO DE CAPA
              <div className="textoeditar2">Max - 1mb - Coloque uma imagem do seu vencedor do Oscar preferido.</div>
            </label>
            {fotoCapaURL && (
              <img src={fotoCapaURL} alt="Capa" className="foto-capa" />
            )}
            <input
              type="file"
              accept="image/*"
              id="input-foto-capa"
              style={{ display: "none" }}
              onChange={(e) => {
                const file = e.target.files[0];
                if (file && file.size > 1 * 1024 * 1024) {
                  setPopupMsg("A imagem de capa ultrapassa 1MB.");
                  return;
                }
                setFotoCapaFile(file || null);
                if (file) {
                  setFotoCapaURL(URL.createObjectURL(file));
                } else {
                  setFotoCapaURL("");
                }
                limparErroCampo("fotoCapa");
              }}
            />
            <button
              className={`upload-btn ${errors.fotoCapa ? "input-erro" : ""}`}
              onClick={() => document.getElementById("input-foto-capa").click()}
              aria-invalid={!!errors.fotoCapa}
            >
              UPLOAD
            </button>
            <button
              className="delete-btn"
              onClick={() => {
                setFotoCapaFile(null);
                setFotoCapaURL("");
                setErrors((prev) => ({ ...prev, fotoCapa: "Foto de capa é obrigatória." }));
              }}
            >
              DELETE
            </button>
            {errors.fotoCapa && <div className="hint-erro">{errors.fotoCapa}</div>}
          </div>
        </div>

        {/* Campos comuns */}
        <input
          ref={refs.primeiroNome}
          type="text"
          placeholder="PRIMEIRO NOME"
          value={primeiroNome}
          onChange={(e) => {
            setPrimeiroNome(e.target.value);
            limparErroCampo("primeiroNome");
          }}
          className={errors.primeiroNome ? "input-erro" : ""}
          aria-invalid={!!errors.primeiroNome}
        />
        {errors.primeiroNome && <div className="hint-erro">{errors.primeiroNome}</div>}

        <input
          ref={refs.ultimoNome}
          type="text"
          placeholder="ÚLTIMO NOME"
          value={ultimoNome}
          onChange={(e) => {
            setUltimoNome(e.target.value);
            limparErroCampo("ultimoNome");
          }}
          className={errors.ultimoNome ? "input-erro" : ""}
          aria-invalid={!!errors.ultimoNome}
        />
        {errors.ultimoNome && <div className="hint-erro">{errors.ultimoNome}</div>}

        {/* ESTADO — agora como dropdown */}
        <div ref={refs.estado}>
          <select
            value={estado}
            onChange={(e) => {
              setEstado(e.target.value);
              limparErroCampo("estado");
            }}
            className={errors.estado ? "input-erro" : ""}
            aria-invalid={!!errors.estado}
          >
            <option value="">ESTADO</option>
            <option value="AC">Acre</option>
            <option value="AL">Alagoas</option>
            <option value="AP">Amapá</option>
            <option value="AM">Amazonas</option>
            <option value="BA">Bahia</option>
            <option value="CE">Ceará</option>
            <option value="DF">Distrito Federal</option>
            <option value="ES">Espírito Santo</option>
            <option value="GO">Goiás</option>
            <option value="MA">Maranhão</option>
            <option value="MT">Mato Grosso</option>
            <option value="MS">Mato Grosso do Sul</option>
            <option value="MG">Minas Gerais</option>
            <option value="PA">Pará</option>
            <option value="PB">Paraíba</option>
            <option value="PR">Paraná</option>
            <option value="PE">Pernambuco</option>
            <option value="PI">Piauí</option>
            <option value="RJ">Rio de Janeiro</option>
            <option value="RN">Rio Grande do Norte</option>
            <option value="RS">Rio Grande do Sul</option>
            <option value="RO">Rondônia</option>
            <option value="RR">Roraima</option>
            <option value="SC">Santa Catarina</option>
            <option value="SP">São Paulo</option>
            <option value="SE">Sergipe</option>
            <option value="TO">Tocantins</option>
          </select>
          {errors.estado && <div className="hint-erro">{errors.estado}</div>}
        </div>

        {/* Campos de especialista */}
        {especialista && (
          <>
            <div className="area-especialista-titulo">ÁREA ESPECIALISTAS</div>

            <label className="textoeditar">
              SOBRE MIM (FALE SOBRE SUA CARREIRA E TRABALHO)
              <div className="textoeditar2">máx. 600 caracteres</div>
            </label>
            <textarea
              ref={refs.sobreMim}
              placeholder="SOBRE MIM"
              value={sobreMim}
              maxLength={600}
              onChange={(e) => {
                setSobreMim(e.target.value);
                limparErroCampo("sobreMim");
              }}
              className={errors.sobreMim ? "input-erro" : ""}
              aria-invalid={!!errors.sobreMim}
            />
            {errors.sobreMim && <div className="hint-erro">{errors.sobreMim}</div>}

            <label className="textoeditar">LINK PARA SEGUIR - NÃO ESQUEÇA DO https://</label>
            <input
              ref={refs.linkParaSeguir}
              type="text"
              placeholder="Ex: link do site, coluna ou rede social"
              value={linkParaSeguir}
              onChange={(e) => {
                setLinkParaSeguir(e.target.value);
                limparErroCampo("linkParaSeguir");
              }}
              className={errors.linkParaSeguir ? "input-erro" : ""}
              aria-invalid={!!errors.linkParaSeguir}
            />
            {errors.linkParaSeguir && <div className="hint-erro">{errors.linkParaSeguir}</div>}

            <label className="textoeditar">DENOMINAÇÃO</label>
            <input
              ref={refs.denominacao}
              type="text"
              placeholder="Ex: jornalista, colunista, youtuber, nome da mídia que representa"
              value={denominacao}
              onChange={(e) => {
                setDenominacao(e.target.value);
                limparErroCampo("denominacao");
              }}
              className={errors.denominacao ? "input-erro" : ""}
              aria-invalid={!!errors.denominacao}
            />
            {errors.denominacao && <div className="hint-erro">{errors.denominacao}</div>}
          </>
        )}

        <button className="btn-salvar" onClick={salvar}>SALVAR</button>
      </div>

      {popupMsg && (
        <PopupMensagem mensagem={popupMsg} onClose={() => setPopupMsg(null)} />
      )}
    </div>
  );
}

export default EditarPerfilPopup;
